import { useCallback, useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ProgressBar from "@atlaskit/progress-bar";

import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import ErrorBoundary from "@/components/error-boundary";
import { useSearchParameters } from "@/components/signup/hooks";
import {
    UfoCustomTimingKey,
    ufoEventOnboardingResolveTenantId
} from "@/components/signup/utils";
import { ExperimentView, experiments } from "@/utils/experiment";

import { JiraOnboardingContainer } from "../../components/jira-onboarding-container/jira-onboarding-container";
import { RocketAnimation } from "../../components/rocket-animation";
import { useCloudStatus } from "../../hooks";
import { useScreenManager } from "../../hooks/use-screen-manager";
import { useTenantAwareStatsigClientUpdater } from "../../hooks/use-tenant-aware-statsig-client-updater";
import { SkipScreen } from "../../utils/screen-manager/skip-screen";

import messages from "./messages";

const ANALYTICS_PREFIX = "resolveTenant";

export const ResolveTenant = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleOnError = useCallback(
        (error: Error) => {
            createAnalyticsEvent({
                eventType: "track",
                actionSubject: ANALYTICS_PREFIX,
                action: "errorBoundary",
                attributes: {
                    error: error.message
                }
            }).fire();
        },
        [createAnalyticsEvent]
    );

    return (
        <ErrorBoundary fallback={<SkipScreen />} onError={handleOnError}>
            <ExperimentView
                experiment={experiments.jiraOnboardingResolveTenantId}
                render={(isEnabled) => {
                    if (isEnabled) {
                        return <LoadingAnimation />;
                    } else {
                        return <SkipScreen />;
                    }
                }}
            />
        </ErrorBoundary>
    );
};

const LoadingAnimation = () => {
    const loadAnimationStart = useMemo(() => performance.now(), []);
    const { formatMessage } = useIntl();
    const { signupId } = useSearchParameters();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { skipScreen } = useScreenManager();
    const { updatedStatsigClientWithCloudId } =
        useTenantAwareStatsigClientUpdater();
    const { getCloudStatus } = useCloudStatus();
    const analyticsClient = useAnalyticsClient();

    const [animationStep, setAnimationStep] = useState<
        "pending" | "start" | "fade" | "rocket" | "expand"
    >("pending");

    const [progress, setProgress] = useState(0);

    useEffect(() => {
        // since this event fires on page load, we want to ensure
        // the analytics client is available before firing the event
        if (!analyticsClient) {
            return;
        }

        createAnalyticsEvent({
            action: "viewed",
            eventType: "screen",
            name: `${ANALYTICS_PREFIX}Screen`
        }).fire();
    }, [analyticsClient, createAnalyticsEvent]);

    useEffect(() => {
        if (updatedStatsigClientWithCloudId) {
            createAnalyticsEvent({
                eventType: "track",
                actionSubject: ANALYTICS_PREFIX,
                action: "succeeded",
                attributes: {
                    elapsedMs: performance.now() - loadAnimationStart
                }
            }).fire();
            setProgress(1);
            setAnimationStep("start");
            ufoEventOnboardingResolveTenantId.success();
            return;
        }

        if (animationStep === "pending") {
            ufoEventOnboardingResolveTenantId.start();

            ufoEventOnboardingResolveTenantId.setCustomData({ signupId });

            ufoEventOnboardingResolveTenantId.startSubstep(
                UfoCustomTimingKey.RESOLVE_TENANT_ID
            );

            const progressInterval = setInterval(() => {
                setProgress((prev) => {
                    if (prev <= 0.7) {
                        return prev + 0.005;
                    } else {
                        return prev;
                    }
                });
            }, 100);

            const resolutionExceededTimeout = setTimeout(() => {
                setProgress(1);
                setAnimationStep("start");
                ufoEventOnboardingResolveTenantId.failure({
                    error: "Tenant resolution timeout exceeded."
                });
                createAnalyticsEvent({
                    eventType: "track",
                    actionSubject: ANALYTICS_PREFIX,
                    action: "failed",
                    attributes: {
                        elapsedMs: performance.now() - loadAnimationStart,
                        error: "Tenant resolution timeout exceeded."
                    }
                }).fire();
            }, 60_000);

            return () => {
                clearInterval(progressInterval);
                clearTimeout(resolutionExceededTimeout);
            };
        }

        return;
    }, [updatedStatsigClientWithCloudId]);

    useEffect(() => {
        const timeout = setInterval(() => {
            // manually poll getCloudStatus after 10 seconds in case
            // cloudId is not set from polling yet. This is a fallback to
            // avoid extended wait. The graphQL subscription should normally
            // set the cloudId before this timeout is hit.
            getCloudStatus();
        }, 10_000);

        return () => clearInterval(timeout);
    }, [getCloudStatus]);

    useEffect(() => {
        // step through animation stages with timers
        if (animationStep === "start") {
            const timer = setTimeout(() => setAnimationStep("fade"), 250);
            return () => clearTimeout(timer);
        }

        if (animationStep === "fade") {
            const timer = setTimeout(() => setAnimationStep("rocket"), 1000);
            return () => clearTimeout(timer);
        }

        if (animationStep === "rocket") {
            const timer = setTimeout(() => setAnimationStep("expand"), 1000);
            return () => clearTimeout(timer);
        }

        if (animationStep === "expand") {
            const timer = setTimeout(() => skipScreen(), 500);
            return () => clearTimeout(timer);
        }

        return undefined;
    }, [updatedStatsigClientWithCloudId, animationStep, skipScreen]);

    return (
        <JiraOnboardingContainer
            header={formatMessage(messages.header)}
            subheader={formatMessage(messages.subHeader)}
            fadeOutContent={["fade", "rocket", "expand"].includes(
                animationStep
            )}
            size={animationStep === "expand" ? "lg" : "sm"}
        >
            <div
                aria-label={`animation-step-${animationStep}`}
                data-testid="loading-animation"
                css={[container, animationStep === "expand" && expandContainer]}
            >
                <RocketAnimation
                    animationStep={animationStep}
                    progress={progress}
                />
                <div
                    data-testid="progress-bar-container"
                    css={[
                        progressBarContainer,
                        ["fade", "rocket", "expand"].includes(animationStep) &&
                            opacityFade
                    ]}
                >
                    <ProgressBar
                        data-testid="progress-bar"
                        appearance={progress < 1 ? "default" : "success"}
                        value={progress}
                    />
                </div>
            </div>
        </JiraOnboardingContainer>
    );
};

const container = css({
    height: 150,
    width: "100%",
    minWidth: 300,
    display: "flex",
    alignItems: "center",
    transition: "all 0.4s ease-in-out",
    flexDirection: "column",
    gap: 15
});

const progressBarContainer = css({
    width: "100%"
});

const expandContainer = css({
    height: 550
});

const opacityFade = css({
    opacity: 0,
    transition: "opacity 0.5s ease-in-out"
});
